<template>
  <div class="audio-resource-management">
    <div class="page-header">
      <h2>音频资源管理</h2>
      <p>上传并维护客户端 5/3/1 分钟提醒语音，客户端启动后会自动拉取最新启用音频。</p>
    </div>

    <div class="page-content">
      <div class="toolbar">
        <button class="refresh-btn" @click="loadAudioResources" :disabled="loading">
          {{ loading ? '加载中...' : '刷新列表' }}
        </button>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>提醒位</th>
              <th>当前文件</th>
              <th>版本</th>
              <th>状态</th>
              <th>预览</th>
              <th>更新时间</th>
              <th>替换上传</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="empty-cell">加载中...</td>
            </tr>
            <tr v-else-if="audioResources.length === 0">
              <td colspan="7" class="empty-cell">暂无音频资源</td>
            </tr>
            <tr v-else v-for="item in audioResources" :key="item.id">
              <td>{{ item.slotName }}</td>
              <td>
                <div class="file-block">
                  <span>{{ item.originalFileName || '未上传' }}</span>
                  <span v-if="item.fileSize" class="file-meta">{{ formatFileSize(item.fileSize) }}</span>
                </div>
              </td>
              <td>{{ item.version || '-' }}</td>
              <td>
                <label class="switch-label">
                  <input
                    type="checkbox"
                    :checked="item.enabled"
                    @change="handleStatusChange(item, $event)"
                  />
                  <span>{{ item.enabled ? '启用' : '停用' }}</span>
                </label>
              </td>
              <td>
                <audio
                  v-if="item.accessUrl"
                  :src="getPreviewUrl(item.accessUrl)"
                  controls
                  preload="none"
                  class="audio-player"
                ></audio>
                <span v-else>-</span>
              </td>
              <td>{{ formatDate(item.updatedTime) }}</td>
              <td>
                <div class="upload-block">
                  <input
                    type="file"
                    accept=".wav,audio/wav"
                    @change="handleFileChange(item.slotCode, $event)"
                  />
                  <button
                    class="upload-btn"
                    @click="uploadAudio(item.slotCode)"
                    :disabled="uploadingSlots[item.slotCode]"
                  >
                    {{ uploadingSlots[item.slotCode] ? '上传中...' : '上传替换' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'AudioResourceManagement',
  data() {
    return {
      loading: false,
      audioResources: [],
      selectedFiles: {},
      uploadingSlots: {}
    }
  },
  mounted() {
    this.loadAudioResources()
  },
  methods: {
    async loadAudioResources() {
      this.loading = true
      try {
        const response = await request.get('/api/audio-resources/admin/list')
        if (response.data.success && response.data.code === 200) {
          this.audioResources = response.data.data || []
        } else {
          throw new Error(response.data.message || '获取音频资源失败')
        }
      } catch (error) {
        console.error('获取音频资源失败:', error)
        alert(error.message || '获取音频资源失败，请重试')
      } finally {
        this.loading = false
      }
    },
    handleFileChange(slotCode, event) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.selectedFiles = {
        ...this.selectedFiles,
        [slotCode]: file
      }
    },
    async uploadAudio(slotCode) {
      const file = this.selectedFiles[slotCode]
      if (!file) {
        alert('请先选择 WAV 音频文件')
        return
      }

      const formData = new FormData()
      formData.append('slotCode', slotCode)
      formData.append('file', file)

      this.uploadingSlots = {
        ...this.uploadingSlots,
        [slotCode]: true
      }

      try {
        const response = await request.post('/api/audio-resources/admin/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.data.success && response.data.code === 200) {
          alert('上传成功')
          await this.loadAudioResources()
          this.selectedFiles = {
            ...this.selectedFiles,
            [slotCode]: null
          }
        } else {
          throw new Error(response.data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传音频失败:', error)
        alert(error.message || '上传音频失败，请重试')
      } finally {
        this.uploadingSlots = {
          ...this.uploadingSlots,
          [slotCode]: false
        }
      }
    },
    async handleStatusChange(item, event) {
      const enabled = !!event.target.checked
      try {
        const response = await request.put('/api/audio-resources/admin/status', null, {
          params: {
            id: item.id,
            enabled
          }
        })
        if (response.data.success && response.data.code === 200) {
          item.enabled = enabled
        } else {
          throw new Error(response.data.message || '状态更新失败')
        }
      } catch (error) {
        console.error('状态更新失败:', error)
        event.target.checked = !enabled
        alert(error.message || '状态更新失败，请重试')
      }
    },
    getPreviewUrl(accessUrl) {
      return accessUrl || ''
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('zh-CN')
    },
    formatFileSize(size) {
      if (!size) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(2)} MB`
    }
  }
}
</script>

<style scoped>
.audio-resource-management {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.toolbar {
  margin-bottom: 16px;
}

.refresh-btn,
.upload-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
}

.refresh-btn {
  background: #007bff;
}

.upload-btn {
  background: #28a745;
}

.refresh-btn:disabled,
.upload-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
  vertical-align: middle;
}

.data-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.empty-cell {
  text-align: center;
  color: #666;
  padding: 32px;
}

.file-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-meta {
  color: #999;
  font-size: 12px;
}

.switch-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.audio-player {
  width: 220px;
  max-width: 100%;
}

.upload-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
}
</style>
